import React, { useState, useEffect, useCallback } from 'react'
import API from '../../../service/api'
import API_BOOK from '../../../service/endpoints'
import './NonCommLogsManager.css'

const ENDPOINTS = API_BOOK.REPORTS.NON_COMM_LOGS_ADMIN
const PAGE_SIZE = 50

const formatDuration = (seconds) => {
  if (!seconds) return '-'
  const days = Math.floor(seconds / 86400)
  const hrs = Math.floor((seconds % 86400) / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const parts = []
  if (days > 0) parts.push(days + 'd')
  if (hrs > 0) parts.push(hrs + 'h')
  parts.push(mins + 'm')
  return parts.join(' ')
}

const getIssueBadgeClass = (issue) => {
  if (!issue) return 'nclm-issue-default'
  const lower = issue.toLowerCase()
  if (lower.includes('plug')) return 'nclm-issue-plugout'
  if (lower.includes('network')) return 'nclm-issue-network'
  if (lower.includes('device')) return 'nclm-issue-device'
  if (lower.includes('gps') || lower.includes('inactive')) return 'nclm-issue-gps'
  return 'nclm-issue-default'
}

const getDurationClass = (seconds) => {
  if (!seconds) return ''
  const hours = seconds / 3600
  if (hours > 168) return 'nclm-duration-critical'
  if (hours > 24) return 'nclm-duration-warn'
  return ''
}

const NonCommLogsManager = () => {
  const [summary, setSummary] = useState(null)
  const [logs, setLogs] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [searchKey, setSearchKey] = useState('')
  const [filterOem, setFilterOem] = useState('')
  const [filterIssue, setFilterIssue] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const [confirmAction, setConfirmAction] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  const fetchSummary = useCallback(async () => {
    try {
      setSummaryLoading(true)
      const res = await API({ url: ENDPOINTS.SUMMARY.url, method: ENDPOINTS.SUMMARY.method, manageLoader: false })
      if (res?.data?.data) setSummary(res.data.data)
    } catch (err) {
      console.error('Failed to fetch summary', err)
    } finally {
      setSummaryLoading(false)
    }
  }, [])

  const fetchLogs = useCallback(async (page = 0) => {
    try {
      setLoading(true)
      const params = { pageSize: PAGE_SIZE, pageIndex: page }
      if (searchKey) params.searchKey = searchKey
      if (filterOem) params.oem = filterOem
      if (filterIssue) params.issue = filterIssue
      const res = await API({ url: ENDPOINTS.LIST.url, method: ENDPOINTS.LIST.method, params, manageLoader: false })
      if (res?.data?.data) {
        setLogs(res.data.data.rows || [])
        setTotalCount(res.data.data.count || 0)
      }
    } catch (err) {
      console.error('Failed to fetch logs', err)
    } finally {
      setLoading(false)
    }
  }, [searchKey, filterOem, filterIssue])

  useEffect(() => { fetchSummary() }, [fetchSummary])
  useEffect(() => { fetchLogs(pageIndex) }, [fetchLogs, pageIndex])

  const handleSearch = (e) => {
    e.preventDefault()
    setPageIndex(0)
    fetchLogs(0)
  }

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === logs.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(logs.map(l => l._id)))
    }
  }

  const handleBulkClose = async () => {
    try {
      setActionLoading(true)
      const ids = Array.from(selectedIds)
      await API({ url: ENDPOINTS.BULK_CLOSE.url, method: ENDPOINTS.BULK_CLOSE.method, data: { ids }, manageLoader: false })
      setSelectedIds(new Set())
      setConfirmAction(null)
      await Promise.all([fetchSummary(), fetchLogs(pageIndex)])
    } catch (err) {
      console.error('Bulk close failed', err)
    } finally {
      setActionLoading(false)
    }
  }

  const handleSoftDelete = async () => {
    try {
      setActionLoading(true)
      const ids = Array.from(selectedIds)
      await API({ url: ENDPOINTS.SOFT_DELETE.url, method: ENDPOINTS.SOFT_DELETE.method, data: { ids }, manageLoader: false })
      setSelectedIds(new Set())
      setConfirmAction(null)
      await Promise.all([fetchSummary(), fetchLogs(pageIndex)])
    } catch (err) {
      console.error('Soft delete failed', err)
    } finally {
      setActionLoading(false)
    }
  }

  const handleFilterByCard = (oem, issue) => {
    setFilterOem(oem || '')
    setFilterIssue(issue || '')
    setPageIndex(0)
    setSelectedIds(new Set())
  }

  const handleRefresh = () => {
    setSelectedIds(new Set())
    fetchSummary()
    fetchLogs(pageIndex)
  }

  const oemList = summary?.groups
    ? [...new Set(summary.groups.map(g => g._id.oem).filter(Boolean))].sort()
    : []

  const issueList = summary?.groups
    ? [...new Set(summary.groups.map(g => g._id.issue).filter(Boolean))].sort()
    : []

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  return (
    <div className="nclm-container">
      <div className="nclm-header">
        <h5 className="nclm-title">Non-Comm Logs Manager</h5>
        <button className="nclm-btn nclm-btn-refresh" onClick={handleRefresh}>Refresh</button>
      </div>

      {/* Summary Cards */}
      {summaryLoading && <div className="nclm-loading">Loading summary...</div>}
      {summary && (
        <div className="nclm-summary-grid">
          <div
            className={`nclm-summary-card ${!filterOem && !filterIssue ? 'active' : ''}`}
            onClick={() => handleFilterByCard('', '')}
          >
            <div className="nclm-summary-label">Total Open</div>
            <div className="nclm-summary-count">{summary.total}</div>
          </div>
          {summary.groups.map((g, i) => (
            <div
              key={i}
              className={`nclm-summary-card ${filterOem === g._id.oem && filterIssue === g._id.issue ? 'active' : ''}`}
              onClick={() => handleFilterByCard(g._id.oem, g._id.issue)}
            >
              <div className="nclm-summary-label">{g._id.oem}</div>
              <div className="nclm-summary-count">{g.count}</div>
              <div className="nclm-summary-sub">{g._id.issue}</div>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="nclm-toolbar">
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px' }}>
          <input
            className="nclm-input"
            type="text"
            placeholder="Search RN / Chassis..."
            value={searchKey}
            onChange={e => setSearchKey(e.target.value)}
          />
          <button type="submit" className="nclm-btn nclm-btn-refresh">Search</button>
        </form>

        <select className="nclm-select" value={filterOem} onChange={e => { setFilterOem(e.target.value); setPageIndex(0) }}>
          <option value="">All OEMs</option>
          {oemList.map(o => <option key={o} value={o}>{o}</option>)}
        </select>

        <select className="nclm-select" value={filterIssue} onChange={e => { setFilterIssue(e.target.value); setPageIndex(0) }}>
          <option value="">All Issues</option>
          {issueList.map(o => <option key={o} value={o}>{o}</option>)}
        </select>

        <button
          className="nclm-btn nclm-btn-select-all"
          onClick={toggleSelectAll}
        >
          {selectedIds.size === logs.length && logs.length > 0 ? 'Deselect All' : 'Select All'}
        </button>

        <button
          className="nclm-btn nclm-btn-close"
          disabled={selectedIds.size === 0}
          onClick={() => setConfirmAction('close')}
        >
          Close Selected ({selectedIds.size})
        </button>

        <button
          className="nclm-btn nclm-btn-delete"
          disabled={selectedIds.size === 0}
          onClick={() => setConfirmAction('delete')}
        >
          Soft Delete ({selectedIds.size})
        </button>

        <span className="nclm-count-label">{totalCount} records</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="nclm-loading">Loading logs...</div>
      ) : logs.length === 0 ? (
        <div className="nclm-empty">No open non-comm logs found</div>
      ) : (
        <div className="nclm-table-wrap">
          <table className="nclm-table">
            <thead>
              <tr>
                <th><input type="checkbox" className="nclm-checkbox" checked={selectedIds.size === logs.length && logs.length > 0} onChange={toggleSelectAll} /></th>
                <th>#</th>
                <th>Reg. Number</th>
                <th>OEM</th>
                <th>Model</th>
                <th>Issue</th>
                <th>Start DateTime (IST)</th>
                <th>Duration</th>
                <th>City</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => {
                const startIST = log.startDateTime
                  ? new Date(log.startDateTime).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })
                  : '-'
                return (
                  <tr key={log._id} className={selectedIds.has(log._id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        className="nclm-checkbox"
                        checked={selectedIds.has(log._id)}
                        onChange={() => toggleSelect(log._id)}
                      />
                    </td>
                    <td>{pageIndex * PAGE_SIZE + i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{log.registration_number || '-'}</td>
                    <td>{log.oem || '-'}</td>
                    <td>{log.model || '-'}</td>
                    <td>
                      <span className={`nclm-issue-badge ${getIssueBadgeClass(log.issue)}`}>
                        {log.issue || '-'}
                      </span>
                    </td>
                    <td>{startIST}</td>
                    <td className={getDurationClass(log.duration)}>
                      {formatDuration(log.duration)}
                    </td>
                    <td>{log.city || '-'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="nclm-pagination">
          <button className="nclm-btn-page" disabled={pageIndex === 0} onClick={() => setPageIndex(p => p - 1)}>Prev</button>
          <span className="nclm-page-info">Page {pageIndex + 1} of {totalPages}</span>
          <button className="nclm-btn-page" disabled={pageIndex >= totalPages - 1} onClick={() => setPageIndex(p => p + 1)}>Next</button>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="nclm-confirm-overlay">
          <div className="nclm-confirm-box">
            <div className="nclm-confirm-title">
              {confirmAction === 'close' ? 'Close Non-Comm Logs' : 'Soft Delete Non-Comm Logs'}
            </div>
            <div className="nclm-confirm-msg">
              {confirmAction === 'close'
                ? `Are you sure you want to close ${selectedIds.size} selected non-comm log(s)? This will set status to "Close" with current timestamp as end time.`
                : `Are you sure you want to soft delete ${selectedIds.size} selected non-comm log(s)? They will be marked as deleted and closed.`
              }
            </div>
            <div className="nclm-confirm-actions">
              <button className="nclm-btn nclm-btn-cancel" onClick={() => setConfirmAction(null)} disabled={actionLoading}>Cancel</button>
              <button
                className={`nclm-btn ${confirmAction === 'close' ? 'nclm-btn-close' : 'nclm-btn-delete'}`}
                onClick={confirmAction === 'close' ? handleBulkClose : handleSoftDelete}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NonCommLogsManager
